import { useState } from "react";
import { ChevronDown } from "lucide-react";

export interface FaqEntry {
  question: string;
  answer: string;
  /** Optional official source shown as a link under the answer. */
  source?: { label: string; url: string };
}

const K401_LINK = "401(k) Calculator";

/** Renders "401(k) Calculator" in an answer as a link to that tool; everything else stays plain text. */
function AnswerText({ text }: { text: string }) {
  const i = text.indexOf(K401_LINK);
  if (i < 0) return <>{text}</>;
  return (
    <>
      {text.slice(0, i)}
      <a href="/tool/401k-calculator" className="underline text-primary">{K401_LINK}</a>
      {text.slice(i + K401_LINK.length)}
    </>
  );
}

function SourceLink({ source }: { source?: { label: string; url: string } }) {
  if (!source) return null;
  return (
    <>
      {" "}
      <a href={source.url} target="_blank" rel="noopener noreferrer" className="underline text-primary">
        Official source: {source.label}
      </a>
    </>
  );
}

/**
 * Renders a real, visible FAQ accordion -- paired with useFaqSchema
 * (called separately by the page, since the schema and the visual
 * rendering are different concerns) for the structured-data half of
 * the same content. Collapsed by default with one entry open at a
 * time, matching the compact, mobile-friendly pattern already
 * established with CollapsibleText rather than a wall of open text.
 */
export function FaqSection({ entries }: { entries: FaqEntry[] | undefined }) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  if (!entries || entries.length === 0) return null;

  return (
    <div className="mt-8">
      <h2 className="text-lg font-semibold mb-3">Frequently asked questions</h2>
      <div className="space-y-2">
        {entries.map((entry, i) => {
          const isOpen = openIndex === i;
          return (
            <div key={i} className="card-raised rounded-xl overflow-hidden">
              <button
                type="button"
                onClick={() => setOpenIndex(isOpen ? null : i)}
                className="w-full flex items-center justify-between gap-3 p-4 text-left"
                aria-expanded={isOpen}
              >
                <span className="text-sm font-medium">{entry.question}</span>
                <ChevronDown
                  className={`h-4 w-4 text-muted-foreground shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`}
                />
              </button>
              {/* Always in the page (just hidden while closed) so the answer text is
                  present for search engines and screen-reader search. */}
              <p hidden={!isOpen} className="px-4 pb-4 text-sm text-muted-foreground leading-relaxed">
                <AnswerText text={entry.answer} />
                <SourceLink source={entry.source} />
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** Plain question/answer list with no accordion, for use inside another collapsible panel. */
export function FaqList({ entries }: { entries: FaqEntry[] | undefined }) {
  if (!entries || entries.length === 0) return null;
  return (
    <dl className="space-y-4 max-w-3xl">
      {entries.map((entry, i) => (
        <div key={i}>
          <dt className="text-sm font-medium">{entry.question}</dt>
          <dd className="mt-1 text-sm text-muted-foreground leading-relaxed"><AnswerText text={entry.answer} /><SourceLink source={entry.source} /></dd>
        </div>
      ))}
    </dl>
  );
}
